import { useStarCommandNative } from './useStarCommandNative.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { questionsForReply } from '../clarificationQuestions.js';
import { clearAttentionAnswers } from './AttentionQuestionCard.js';
import { attentionApi, gyroAttentionAnswerDriver } from './client.js';
import { attentionMetadata } from './metadata.js';
import { useAttentionInbox } from './useAttentionInbox.js';
import { attentionIntegrationViews } from './AttentionIntegrationViews.js';
export function useAttentionIntegration(options) {
    const { client, desktopWorkspace, workspace, serviceKey, userKey, sessionEpoch, petsEnabled, missionSession, messages, sessions, busy, input, staticAvatar, parts } = options;
    const conversationId = options.sessionIdRef.current;
    const deviceUnreadCount = options.notificationEntries.filter(entry => !entry.read).length;
    const livePorts = {
        composer: () => ({ conversationId: options.sessionIdRef.current, taskRun: options.taskRunRef.current, busy, input,
            inputValue: options.inputRef.current?.value ?? '', pendingAttachments: options.attachmentPendingRef.current,
            attachmentCount: options.attachmentsRef.current.length }),
        canPrepareReply: id => options.conversationsRef.current.get(id)?.state !== 'running' && options.transcriptLoadRef.current.canWrite(id),
        loadSession: options.loadSession, setInput: options.setInput,
        focusComposer: () => requestAnimationFrame(() => options.inputRef.current?.focus()),
        showChat: options.showChat, openPets: options.openPets,
        openDeviceHistory: () => { options.setShowPets(false); options.openNotifications(); }, disablePets: options.disablePets,
    };
    const ports = useRef(livePorts);
    ports.current = livePorts;
    const owner = JSON.stringify([serviceKey, userKey, sessionEpoch]);
    const ownerRef = useRef(owner);
    ownerRef.current = owner;
    const [capability, setCapability] = useState(null);
    // The desktop shell mounts the element with no user key (shell.html; the agent authenticates the one signed-in person by
    // device on every call), so the widget's user key is 'anon' there and must not gate the center: with the gate every PC
    // kept the center dark while the mascot's bell counted the same items (owner, 2026-10-26, "clicking the bell opens the
    // chat and does not switch to the notification panel"). The owner key still carries the service, user and epoch, so an
    // account change on a keyed host clears the inbox as before.
    const enabled = desktopWorkspace && capability?.owner === owner && capability.enabled;
    const enabledRef = useRef(Boolean(enabled));
    enabledRef.current = Boolean(enabled);
    const starCommand = useStarCommandNative({ desktopWorkspace, enabled: Boolean(enabled), accountKey: owner });
    const [open, setOpen] = useState(false);
    const [source, setSource] = useState('all');
    const [navigationNotice, setNavigationNotice] = useState('');
    const [reply, setReply] = useState(null);
    const native = useRef(null);
    const pendingOpen = useRef(null);
    const invalidateNative = useCallback(() => {
        const session = native.current;
        if (session?.owner === ownerRef.current && session.enabled) {
            try {
                window.dispatchEvent(new CustomEvent('gyro:attention-invalidate', { detail: { generation: session.generation } }));
            }
            catch { /* Native polling reconciles the cue. */ }
        }
    }, []);
    const transport = useMemo(() => attentionApi(client, petsEnabled), [client, owner, petsEnabled]);
    const driver = useMemo(() => {
        const authority = gyroAttentionAnswerDriver(client);
        return {
            answer: async (...args) => { const result = await authority.answer(...args); if (!args[2].aborted && ownerRef.current === owner)
                invalidateNative(); return result; },
            receipt: async (...args) => { const result = await authority.receipt(...args); if (!args[2].aborted && ownerRef.current === owner)
                invalidateNative(); return result; },
        };
    }, [client, owner, invalidateNative]);
    const inbox = useAttentionInbox({ api: transport, accountKey: owner, enabled: Boolean(enabled), onAcknowledged: invalidateNative });
    const refreshRef = useRef(inbox.refresh);
    refreshRef.current = inbox.refresh;
    useEffect(() => () => clearAttentionAnswers(driver, owner), [driver, owner]);
    useEffect(() => {
        const previous = native.current;
        if (previous && previous.owner !== owner) {
            window.dispatchEvent(new CustomEvent('gyro:attention-clear', { detail: { generation: previous.generation } }));
            native.current = null;
            pendingOpen.current = null;
        }
        setOpen(false);
        setSource('all');
        setNavigationNotice('');
        setReply(null);
    }, [owner]);
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        const session = (event) => {
            const value = event.detail;
            if (!value || typeof value !== 'object')
                return;
            const record = value;
            if (typeof record.generation !== 'string' || !/^[a-f0-9]{32}$/.test(record.generation) || typeof record.enabled !== 'boolean' || typeof record.petsAvailable !== 'boolean')
                return;
            native.current = { owner, generation: record.generation, enabled: record.enabled, petsAvailable: record.petsAvailable };
        };
        const openFromMascot = (event) => {
            const value = event.detail, session = native.current;
            if (!session?.enabled || session.owner !== owner || !value || typeof value !== 'object')
                return;
            const record = value;
            if (record.generation !== session.generation || !record.source)
                return;
            const target = record.source;
            if (!enabled) {
                if (target.kind === 'all' || target.kind === 'gyro')
                    pendingOpen.current = { generation: session.generation, source: { kind: target.kind } };
                else if (target.kind === 'pet' && typeof target.petId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(target.petId))
                    pendingOpen.current = { generation: session.generation, source: { kind: target.kind, petId: target.petId } };
                return;
            }
            pendingOpen.current = null;
            if (target.kind === 'pet') {
                if (!petsEnabled || !session.petsAvailable || typeof target.petId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(target.petId))
                    return;
                setSource(`pet:${target.petId}`);
            }
            else if (target.kind === 'all' || target.kind === 'gyro')
                setSource(target.kind === 'all' ? 'all' : 'gyro:gyro');
            else
                return;
            setNavigationNotice('');
            setOpen(true);
            void refreshRef.current();
        };
        window.addEventListener('gyro:attention-session', session);
        window.addEventListener('gyro:attention-open', openFromMascot);
        window.dispatchEvent(new CustomEvent('gyro:attention-ready', { detail: {} }));
        if (enabled && pendingOpen.current)
            window.dispatchEvent(new CustomEvent('gyro:attention-open', { detail: pendingOpen.current }));
        return () => { window.removeEventListener('gyro:attention-session', session); window.removeEventListener('gyro:attention-open', openFromMascot); };
    }, [owner, enabled, desktopWorkspace, petsEnabled]);
    const composerOccupied = () => {
        const current = ports.current.composer();
        return current.busy || current.pendingAttachments > 0 || current.attachmentCount > 0 || Boolean(current.input.trim() || current.inputValue.trim());
    };
    async function openTarget(target) {
        if (!enabled)
            return;
        if (target.kind === 'conversation') {
            const departure = ports.current.composer();
            if (composerOccupied()) {
                setNavigationNotice('Finish or save your current draft before opening the original chat. Your answer stays saved.');
                return;
            }
            if (departure.conversationId !== target.conversationId) {
                let selected = sessions.find(session => session.id === target.conversationId);
                if (!selected) {
                    const full = await client.getHistory(target.conversationId).catch(() => null);
                    if (ownerRef.current !== owner)
                        return;
                    const current = ports.current.composer();
                    if (current.conversationId !== departure.conversationId || current.taskRun !== departure.taskRun)
                        return;
                    if (composerOccupied()) {
                        setNavigationNotice('Your current draft was kept. Open the original chat again when you are ready.');
                        return;
                    }
                    if (!full) {
                        setNavigationNotice('The original chat is unavailable. Your saved answer has not been sent to another chat.');
                        return;
                    }
                    selected = { id: full.id, title: full.title, messages: full.turns, createdAt: full.createdAt, updatedAt: full.updatedAt, app: full.app };
                }
                await ports.current.loadSession(selected);
            }
            if (ownerRef.current !== owner || ports.current.composer().conversationId !== target.conversationId)
                return;
            ports.current.showChat();
            setOpen(false);
            if (reply?.owner === owner && reply.conversationId === target.conversationId && !composerOccupied() && ports.current.canPrepareReply(target.conversationId)) {
                ports.current.setInput(reply.reply);
                setReply(null);
                ports.current.focusComposer();
            }
        }
        else if (target.kind === 'pet-mission' && petsEnabled) {
            setOpen(false);
            ports.current.openPets({ view: 'pet', petId: target.petId, tab: target.decisionId ? 'chat' : 'overview', missionId: target.missionId, decisionId: target.decisionId });
        }
        else if (target.kind === 'pet-brief' && petsEnabled) {
            setOpen(false);
            ports.current.openPets({ view: 'pet', petId: target.petId, tab: 'brief' });
        }
        else if (target.kind === 'pet-activity' && petsEnabled) {
            setOpen(false);
            ports.current.openPets({ view: 'pet', petId: target.petId, tab: 'activity' });
        }
        else
            setNavigationNotice('This item needs its original review screen. It has not approved or started an action.');
    }
    function prepareReply(continuation) {
        if (!enabled)
            return;
        if (continuation.conversationId === ports.current.composer().conversationId && !composerOccupied() && ports.current.canPrepareReply(continuation.conversationId)) {
            ports.current.setInput(continuation.reply);
            setOpen(false);
            ports.current.showChat();
            ports.current.focusComposer();
        }
        else {
            setReply({ owner, ...continuation });
            setNavigationNotice('Answer saved. Open the original chat to prepare your reply, then press Send to continue.');
        }
    }
    const conversationItems = enabled && inbox.fresh ? (inbox.snapshot?.items ?? []).filter(item => item.question?.authority.kind === 'gyro-clarification'
        && item.question.authority.conversationId === conversationId) : [];
    const boundIds = new Set(messages.flatMap(message => attentionMetadata(message).attentionQuestionIds ?? []));
    const orphanItems = conversationItems.filter(item => !boundIds.has(item.question.id));
    const replyQuestions = useMemo(() => messages.map(message => message.role === 'assistant' && !message.pending && !message.streaming && !message.error
        && !message.attentionQuestionIds && !message.attentionUnavailable && !enabled
        ? questionsForReply(message.content, message.followups, message.questions) : undefined), [messages, enabled]);
    const dockMessage = workspace && enabled && messages.at(-1)?.role === 'assistant'
        && attentionMetadata(messages.at(-1)).attentionQuestionIds?.some(id => {
            const item = conversationItems.find(value => value.question?.id === id);
            return !item || item.question?.state === 'open';
        }) ? messages.at(-1) : undefined;
    const views = attentionIntegrationViews({ starCommand, enabled: Boolean(enabled), owner, open, inbox, driver, client, missionSession, petsEnabled,
        busy, staticAvatar, deviceUnreadCount, parts, source, navigationNotice, reply: reply?.owner === owner ? reply : null,
        conversationItems, orphanItems, dockMessage, onClose: () => setOpen(false), openTarget, prepareReply, invalidateNative,
        onToggle: () => { setSource('all'); setOpen(value => !value); void inbox.refresh(); },
        onPetsDisabled: () => ports.current.disablePets(), onOpenDeviceHistory: () => { setOpen(false); ports.current.openDeviceHistory(); } });
    return {
        ...views, enabled: Boolean(enabled), replyQuestions, metadata: attentionMetadata,
        settings: enabled ? { ownerKey: owner, petsAvailable: petsEnabled } : undefined,
        /**
         * Open the notification center from the page itself (the nav's Notifications entry, the command palette), the
         * same center the mascot's bell opens: every source, fresh. Before 2026-10-26 those entries opened the device
         * notification history, which never lists Star Command items or questions, so people saw the bell count the
         * mascot and an empty "Notifications" page (owner, 2026-10-26: "this is happening to EVERYONE").
         */
        openCenter: () => {
            if (!enabledRef.current || ownerRef.current !== owner)
                return false;
            setSource('all');
            setNavigationNotice('');
            setOpen(true);
            void refreshRef.current();
            return true;
        },
        acceptCapability: (value) => setCapability({ owner, enabled: value === true }),
        // Background chats also refresh the bell, but an old owner's completion cannot refresh the next owner's inbox.
        refreshAfterAnswer: (result) => {
            if (!enabledRef.current || ownerRef.current !== owner)
                return;
            const metadata = attentionMetadata(result);
            if (metadata.attentionQuestionIds?.length || metadata.attentionUnavailable)
                void refreshRef.current();
        },
        clear: () => {
            enabledRef.current = false;
            clearAttentionAnswers(driver, owner);
            if (native.current?.owner === owner)
                window.dispatchEvent(new CustomEvent('gyro:attention-clear', { detail: { generation: native.current.generation } }));
            native.current = null;
            setCapability(null);
            setOpen(false);
        },
    };
}
//# sourceMappingURL=useAttentionIntegration.js.map